import { Component } from '@theme/component';

/**
 * Fullscreen lightbox for the sk-gallery section.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLImageElement} image
 * @property {HTMLElement} counter
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 *
 * @extends {Component<Refs>}
 */
export class SkGalleryLightbox extends Component {
  requiredRefs = ['dialog', 'image', 'counter'];

  /** @type {{ src: string, alt: string }[]} */
  #items = [];
  #index = 0;
  #touchStartX = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#items = Array.from(this.querySelectorAll('template[data-gallery-item]')).map((template) => ({
      src: template.getAttribute('data-src') ?? '',
      alt: template.getAttribute('data-alt') ?? '',
    }));

    this.refs.dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.refs.dialog.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    this.refs.dialog.removeEventListener('touchstart', this.#handleTouchStart);
    this.refs.dialog.removeEventListener('touchend', this.#handleTouchEnd);
  }

  /**
   * Opens the lightbox at a given image index.
   * @param {number} index
   */
  open(index) {
    this.#index = Number(index) || 0;
    this.#render();
    this.refs.dialog.showModal();
  }

  close() {
    this.refs.dialog.close();
  }

  prev() {
    if (this.#index <= 0) return;
    this.#index -= 1;
    this.#render();
  }

  next() {
    if (this.#index >= this.#items.length - 1) return;
    this.#index += 1;
    this.#render();
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowLeft') {
      this.prev();
    } else if (event.key === 'ArrowRight') {
      this.next();
    }
  }

  #render() {
    const item = this.#items[this.#index];
    if (!item) return;

    const { image, counter, prevButton, nextButton } = this.refs;
    image.src = item.src;
    image.alt = item.alt;
    counter.textContent = `${this.#index + 1} / ${this.#items.length}`;

    if (prevButton) prevButton.disabled = this.#index === 0;
    if (nextButton) nextButton.disabled = this.#index === this.#items.length - 1;

    // Disabling the focused button moves focus to <body>, taking it outside the
    // dialog subtree and silently breaking keyboard navigation. Restore focus to
    // the dialog itself whenever that happens.
    if (!this.refs.dialog.contains(document.activeElement)) {
      this.refs.dialog.focus();
    }
  }

  #handleTouchStart = (/** @type {TouchEvent} */ event) => {
    this.#touchStartX = event.changedTouches[0].screenX;
  };

  #handleTouchEnd = (/** @type {TouchEvent} */ event) => {
    const deltaX = event.changedTouches[0].screenX - this.#touchStartX;
    if (Math.abs(deltaX) < 40) return;

    if (deltaX > 0) {
      this.prev();
    } else {
      this.next();
    }
  };
}

if (!customElements.get('sk-gallery-lightbox')) {
  customElements.define('sk-gallery-lightbox', SkGalleryLightbox);
}
